import {
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { Button, type ButtonVariant } from "../Button";
import { cx } from "../cx";
import { Icon } from "../Icon";
import styles from "./Modal.module.css";

export type ModalSize = "sm" | "md" | "lg";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children?: ReactNode;
  /** sm = 500px (Create project), md = 600px (Edit time entry), lg = 800px (Add members). */
  size?: ModalSize;
  /** Replaces the default Cancel + confirm footer. Pass `null` for no footer. */
  footer?: ReactNode | null;
  confirmLabel?: ReactNode;
  onConfirm?: () => void;
  confirmVariant?: ButtonVariant;
  confirmLoading?: boolean;
  confirmDisabled?: boolean;
  cancelLabel?: ReactNode;
  hideCancel?: boolean;
  /** Wraps body + footer in a <form>, so Enter in a field submits through `onConfirm`. */
  asForm?: boolean;
  closeOnEscape?: boolean;
  closeOnBackdrop?: boolean;
  initialFocusRef?: RefObject<HTMLElement | null>;
  className?: string;
  bodyClassName?: string;
}

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.hasAttribute("inert"));
}

export function Modal({
  open,
  onClose,
  title,
  children,
  size = "sm",
  footer,
  confirmLabel = "Save",
  onConfirm,
  confirmVariant = "primary",
  confirmLoading,
  confirmDisabled,
  cancelLabel = "Cancel",
  hideCancel,
  asForm,
  closeOnEscape = true,
  closeOnBackdrop = true,
  initialFocusRef,
  className,
  bodyClassName,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const pressStartedOnBackdrop = useRef(false);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    const dialog = dialogRef.current;
    if (dialog) {
      const target =
        initialFocusRef?.current ??
        dialog.querySelector<HTMLElement>("[data-autofocus]") ??
        // Skip the header close button so typing can start in the first field, like ngbModal.
        focusables(dialog).find((el) => !el.dataset.modalClose) ??
        dialog;
      target.focus();
    }
    return () => {
      document.body.style.overflow = overflow;
      // The opener may have unmounted meanwhile (e.g. a row deleted from its own menu).
      if (previous && document.contains(previous)) previous.focus();
    };
  }, [open, initialFocusRef]);

  if (!open) return null;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && closeOnEscape) {
      e.stopPropagation();
      onClose();
      return;
    }
    if (e.key !== "Tab" || !dialogRef.current) return;
    const items = focusables(dialogRef.current);
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const first = items[0] as HTMLElement;
    const last = items[items.length - 1] as HTMLElement;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === dialogRef.current)) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const onBackdropDown = (e: MouseEvent<HTMLDivElement>) => {
    pressStartedOnBackdrop.current = e.target === e.currentTarget;
  };
  const onBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    // Requiring press and release on the backdrop stops a text selection drag from closing the modal.
    if (closeOnBackdrop && pressStartedOnBackdrop.current && e.target === e.currentTarget) onClose();
    pressStartedOnBackdrop.current = false;
  };

  const defaultFooter =
    onConfirm || !hideCancel ? (
      <>
        {!hideCancel && (
          <Button variant="text" onClick={onClose}>
            {cancelLabel}
          </Button>
        )}
        {onConfirm && (
          <Button
            variant={confirmVariant}
            type={asForm ? "submit" : "button"}
            onClick={asForm ? undefined : onConfirm}
            loading={confirmLoading}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </Button>
        )}
      </>
    ) : null;
  const footerContent = footer === undefined ? defaultFooter : footer;

  const inner = (
    <>
      <div className={cx(styles.body, bodyClassName)}>{children}</div>
      {footerContent !== null && <div className={styles.footer}>{footerContent}</div>}
    </>
  );

  return createPortal(
    // biome-ignore lint/a11y/noStaticElementInteractions: the backdrop is a pointer target; keyboard users close with Escape
    // biome-ignore lint/a11y/useKeyWithClickEvents: Escape is handled on the dialog
    <div
      className={styles.backdrop}
      onMouseDown={onBackdropDown}
      onClick={onBackdropClick}
      data-testid="modal-backdrop"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cx(styles.dialog, styles[size], className)}
        onKeyDown={onKeyDown}
      >
        <div className={styles.header}>
          <h1 id={titleId} className={styles.title}>
            {title}
          </h1>
          <button type="button" className={styles.close} aria-label="Close" data-modal-close="true" onClick={onClose}>
            <Icon name="close" size={20} strokeWidth={1.75} />
          </button>
        </div>
        {asForm ? (
          <form
            className={styles.form}
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              onConfirm?.();
            }}
          >
            {inner}
          </form>
        ) : (
          inner
        )}
      </div>
    </div>,
    document.body,
  );
}
